/**
 * AI Scoring — pure converters and utilities.
 *
 * Stateless helpers that translate between the app's `StartupProfile` / scoring
 * types and the AI scoring edge-function contract. No store, no React: these are
 * safe to re-export from the pure scoring barrel (`./index`).
 *
 * These converters are driven by the live scoring flow in
 * `@/lib/state/score-store` (`triggerAIScoring`).
 */

import type { StartupProfile } from '@/lib/state/profile-store';
import type { ScoreCriteria } from './types';
import type { AIScoreInput, AIScoringStage } from '@/lib/supabase/edge-functions';

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Convert StartupProfile to AIScoreInput
 */
export function profileToScoreInput(profile: StartupProfile): AIScoreInput {
  return {
    name: profile.name || 'Unnamed Startup',
    stage: normalizeStage(profile.stage),
    industry: profile.industry,
    profileData: {
      // Basic info
      name: profile.name,
      tagline: profile.tagline,
      description: profile.description,
      industry: profile.industry,
      stage: profile.stage,
      foundedYear: profile.foundedYear,
      location: profile.location,
      website: profile.website,
      linkedIn: profile.linkedIn,

      // Team
      teamSize: profile.teamSize,
      isSerialFounder: profile.isSerialFounder,
      previousVentures: profile.previousVentures,

      // Problem/Solution
      problem: profile.problem,
      solution: profile.solution,
      targetCustomer: profile.targetCustomer,

      // Traction
      monthlyRevenue: profile.monthlyRevenue,
      userCount: profile.userCount,
      growthRate: profile.growthRate,

      // Market
      marketSize: profile.marketSize,
      competitors: profile.competitors,

      // Funding
      fundingTarget: profile.fundingTarget,
      minimumTicket: profile.minimumTicket,
      syndicatesAllowed: profile.syndicatesAllowed,
      isActivelyRaising: profile.isActivelyRaising,
      fundraisingHistory: profile.fundraisingHistory,

      // Enrichment data (if available)
      swissVerification: profile.swissVerification,
      startuptickerData: profile.startuptickerData,
      websiteEnrichment: profile.websiteEnrichment?.data,

      // Data room status
      dataRoomConnected: profile.dataRoomConnected,
      dataRoomProvider: profile.dataRoomProvider,
    },
    // Note: pitchDeckContent and documentsContent can be added if available
  };
}

/**
 * Normalize stage string to AIScoringStage
 */
function normalizeStage(stage?: string): AIScoringStage {
  if (!stage) return 'seed';

  const normalized = stage.toLowerCase().replace(/_/g, '-');
  const stageMap: Record<string, AIScoringStage> = {
    'pre-seed': 'pre-seed',
    'preseed': 'pre-seed',
    'seed': 'seed',
    'series-a': 'series-a',
    'series_a': 'series-a',
    'seriesa': 'series-a',
    'series-b': 'series-b-plus',
    'series-b-plus': 'series-b-plus',
    'pre-ipo': 'pre-ipo',
  };

  return stageMap[normalized] || 'seed';
}

/**
 * Convert v2 dimension atoms (from ScoreEventReadModel) to the ScoreCriteria
 * format consumed by the founder scoring UI.
 *
 * Phase 116.6: v2 atoms don't carry sub-criterion data, strength/weakness, or
 * risk level. Those fields render as empty/default — UI handles missing data.
 */
export function aiScoreToScoreCriteria(
  dimensions: Array<{
    id: string;
    rawScore: number;
    evidenceLevel: 'verified' | 'partially-verified' | 'unverified';
    confidence: number;
    feedback?: string;
  }>,
): ScoreCriteria[] {
  return dimensions.map((d) => ({
    id: `ai-${d.id}`,
    name: d.id,
    dimension: d.id as ScoreCriteria['dimension'],
    score: d.rawScore,
    maxScore: 100,
    feedback: d.feedback ?? '',
    recommendation: 'Continue strengthening this area',
    // Risk is the INVERSE of the dimension's score — a weak dimension is a risk.
    // Was hardcoded to 'low', which made the scorecard's risk count meaningless
    // (every dimension counted as low, so a weak startup showed "N low"). (Adi
    // 2026-07-22.) Bands mirror the score labels: >=60 strong→low, 40-59→medium,
    // <40 weak→high.
    riskLevel: (d.rawScore >= 60 ? 'low' : d.rawScore >= 40 ? 'medium' : 'high') as ScoreCriteria['riskLevel'],
    evidenceLevel: d.evidenceLevel === 'partially-verified' ? 'uploaded' : d.evidenceLevel as ScoreCriteria['evidenceLevel'],
    verified: d.evidenceLevel === 'verified',
    verificationNote: `AI Confidence: ${d.confidence}%`,
    subScores: [],
  }));
}

// =============================================================================
// Cost Estimation
// =============================================================================

/**
 * Calculate estimated cost for AI scoring
 */
export function estimateScoringCost(profileSize: 'small' | 'medium' | 'large'): {
  inputTokens: number;
  outputTokens: number;
  estimatedCost: number;
} {
  const estimates = {
    small: { input: 10000, output: 4000 },
    medium: { input: 25000, output: 6000 },
    large: { input: 50000, output: 8000 },
  };

  const { input, output } = estimates[profileSize];

  // Claude Sonnet pricing: $3/MTok input, $15/MTok output
  const cost = (input * 3 + output * 15) / 1000000;

  return {
    inputTokens: input,
    outputTokens: output,
    estimatedCost: Math.round(cost * 100) / 100,
  };
}
